/**
 * Pure state-model helpers for the SINGLE open member profile that
 * TabbedAppShell hosts inside its permanent Search tab.
 *
 * There is no separate member tab: AstraConnect enforces PROVIDER FOCUS, so at
 * most ONE member is open at a time and it REUSES the permanent Search tab
 * slot (which relabels to the member's name while open — see TabShellNav's
 * `initials`/`onClose` props). This file owns only the open/replace/retitle/
 * clear state model for that one slot.
 *
 * Kept dependency-free (no React, no react-router types beyond the minimal
 * shape needed) so it's unit-testable in isolation, mirroring
 * tabShellRouting.ts's convention.
 *
 * HISTORY: this used to model an ORDERED LIST of browser-tab-style member tabs
 * with a soft cap + oldest-first eviction. That was collapsed to a single slot
 * when the member tab chip was removed in favor of reusing the Search tab.
 */

/** Shown until a real member name is resolved — a deep-link/hard-refresh to
 * `/members/:id` with no name in hand, or a Worklist row (whose selected-
 * member payload carries no name field at all — see WorklistPage.tsx /
 * PatientPriorityIndexSelectedMember). */
export const PROFILE_TAB_TITLE_PLACEHOLDER = "Patient";

/** Minimal shape of what the open profile pins — deliberately NOT react-router's
 * `Location` type (keeps this file import-free); a real `Location` (pathname
 * + state, ignoring search/hash/key) satisfies this shape structurally. */
export interface ProfileTabLocation {
  pathname: string;
  state?: unknown;
}

export interface OpenProfile {
  /** The `astrana:<astranaId>` / Mongo `_id` / `pp:...` route id — identity key. */
  navId: string;
  /** Search-tab label while open. Starts as PROFILE_TAB_TITLE_PLACEHOLDER until resolved. */
  title: string;
  /** Pinned location (pathname + state) the profile renders while hidden — see
   * TabbedAppShell's `locationStateOverride` plumbing into MemberProfilePage. */
  location: ProfileTabLocation;
}

/**
 * Open a member into the slot, REPLACING whatever was there.
 *
 * - Same navId as the one already open: returns `current` UNCHANGED (same
 *   object reference) so the pinned location/state — and therefore all
 *   in-flight keep-alive state — survives a re-select. The only mutation
 *   allowed is upgrading a placeholder title once a real one is available.
 * - Different navId: the incoming member wins outright. The caller drops the
 *   outgoing profile's mounted subtree, which is the point of the one-member
 *   constraint (see TabbedAppShell's doc comment).
 */
export function openProfileSlot(
  current: OpenProfile | null,
  next: OpenProfile,
): OpenProfile {
  if (!current || current.navId !== next.navId) return next;
  if (
    current.title === PROFILE_TAB_TITLE_PLACEHOLDER &&
    next.title &&
    next.title !== PROFILE_TAB_TITLE_PLACEHOLDER
  ) {
    return { ...current, title: next.title };
  }
  return current;
}

/**
 * Upgrade the open profile's placeholder title once MemberProfilePage resolves
 * a real member name (`onMemberResolved` — see its doc there). No-ops (returns
 * the same reference) if nothing is open, `navId` isn't the open member, the
 * title is already real, or `name` is empty — never downgrades a real title.
 */
export function upgradeProfileTitle(
  current: OpenProfile | null,
  navId: string,
  name: string,
): OpenProfile | null {
  if (!current || !name) return current;
  if (current.navId !== navId) return current;
  if (current.title !== PROFILE_TAB_TITLE_PLACEHOLDER) return current;
  return { ...current, title: name };
}

/**
 * Two-letter (or fewer) initials for the Search tab's avatar chip while a
 * member is open (and for its condensed narrow-container mode — see
 * TabShellNav.tsx). Returns "?" for an empty name so the chip is never blank;
 * a single-word name (or the PROFILE_TAB_TITLE_PLACEHOLDER "Patient") takes
 * its first two letters.
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
